import { AxiosResponse } from "axios";
import { TypeInscricaoDoAlunoNaViagem } from "../../../Models/Domain/Viagem/InscricaoDoAlunoNaViagem";
import { TypeViagem } from "../../../Models/Domain/Viagem/Viagem";
import RequestUtilitario, { BasicMutationAxiosType, BasicQueryType } from "../../../Utils/RequisicaoUtilitario";
import { Axios, CustomAxiosRequestConfig } from "../../Axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import Titulo from "../../../Models/Financeiro/Titulo/Titulo";
import Arquivo from "../../../Models/Pessoa/Sistema/Arquivo";

import { IPagina, IPaginaInfo, PaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import { EnumStatusDaInscricaoDoAlunoNaViagem } from "@/Models/Domain/Viagem/Enum";
import { EnumStatusDaViagem } from "@/Models/Domain/Viagem/StatusDaViagem";
import { Dayjs } from "dayjs";

export class ViagemService {

    public listarViagens(filtro?: TypeViagem){
        return Axios.post<TypeViagem[]>(`quiron/viagem/listar`, filtro);
    }

    public listarViagem(codigo: number){
        return Axios.get<TypeViagem>(`quiron/viagem/${codigo}`);
    }

    public listarTodasInscricoes(codigo: number){
        return Axios.get<TypeInscricaoDoAlunoNaViagem[]>(`quiron/viagem/${codigo}/inscricoes`);
    }

    public listarInscricoesDaViagem(codigo: number){
        return Axios.get<TypeInscricaoDoAlunoNaViagem[]>(`quiron/viagem/${codigo}/inscricoes`);
    }

    public inserirViagem(payload: TypeViagem) {
        return Axios.post<TypeViagem>(`quiron/viagem/`, payload,{
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public atualizarViagem(payload: TypeViagem) {
        return Axios.put<void>(`quiron/viagem/`, payload, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public atualizarInscricoesDaViagem(codigoDaViagem: number, payload: TypeInscricaoDoAlunoNaViagem[]) {
        return Axios.put<void>(`quiron/viagem/${codigoDaViagem}/inscricoes`, payload);
    }

    public ativarViagem(codigo: number) {
        return Axios.patch(`quiron/viagem/${codigo}/ativar`);
    }

    public desativarViagem(codigo: number) {
        return Axios.patch(`quiron/viagem/${codigo}/desativar`);
    }

    public atualizarStatusDaInscricao(codigoDaViagem: number, codigoDaInscricao: number, novoStatus: EnumStatusDaInscricaoDoAlunoNaViagem) {
        return Axios.patch(`quiron/viagem/${codigoDaViagem}/inscricoes/${codigoDaInscricao}/status/${novoStatus}`);
    }

    public static listarArquivosDiversos(codigo: number) {
        return Axios.get<Arquivo[]>(`quiron/viagem/${codigo}/arquivos`);
    }

    public static inserirArquivos(codigo: number, payload: File[]) {
        return Axios.post<void>(`quiron/viagem/${codigo}/arquivos`, payload, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public static removerArquivo(codigoDaViagem: number, codigoDoArquivo?: number) {
        return Axios.delete<void>(`quiron/viagem/${codigoDaViagem}/arquivos/${codigoDoArquivo}` );
    }

}

export function useListar({
    viagem,
    codsCentrosDeCusto,
    codSituacoesInvalidas,
    codViagemIgnorarFiltro,
    ativo,
    dataInicial,
    dataFinal
}: BasicQueryType<
    AxiosResponse<TypeViagem[]>,
    {
        viagem?: TypeViagem;
        ativo?: boolean;
        codsCentrosDeCusto ?:number[];
        codSituacoesInvalidas?:EnumStatusDaViagem[];
        codViagemIgnorarFiltro?:number;
        dataInicial?:Date;
        dataFinal?:Date;
    }
>) {
    const dataRequest = {
        viagem : viagem,
        ativo,
        codsCentrosDeCusto,
        codSituacoesInvalidas,
        codViagemIgnorarFiltro,
        dataInicial : dataInicial ?? null,
        dataFinal : dataFinal ?? null  
    };
    const queryString = RequestUtilitario.objectToQueryString(dataRequest);

    return useQuery({
        queryFn: () => Axios.get<TypeViagem[]>(`quiron/viagem?${queryString}`),
        queryKey: ["Quiron.Viagem.Listar", `Quiron.Viagem.Listar?${queryString}`],
        select: ({ data }) => data,
        staleTime: 1000 * 60 * 0.5, // query ficará valida por 30 segundos
    });
}

/**
 * A rota em questão só pode ser acessada por responsáveis.
 * @returns Um objeto contendo informações da viagem.
 * A propriedade 'inscricoesDisponiveis' lista todas as inscrições que estão disponíveis para o responsável, segregadas pelos alunos sob sua tutela. <br/>
 * Caso a viagem tenha alunos informados e o tamanho de 'inscricoesDisponiveis' seja 0, o servidor retorna uma exceção.
 */
export const useProcurarViagemPelaSenha = ({
    axiosConfig,
    ...props
}: BasicMutationAxiosType<
    { senha: string },
    { axiosConfig?: CustomAxiosRequestConfig },
    AxiosResponse<{ viagem: TypeViagem; alunosInformados: boolean; inscricoesDisponiveis: TypeInscricaoDoAlunoNaViagem[] } | undefined>
>) =>
    useMutation({
        ...props,
        mutationKey: ["Domain.Viagem.ProcurarViagemPelaSenha"],
        mutationFn: ({ senha }) => {
            if (senha.trim() == "") throw "A senha da viagem é obrigatória";
            return Axios.get(`quiron/viagem/area-responsavel/inscricao`, { ...axiosConfig, params: { senha: senha.trim() } });
        },
    });

/**
 */
export const useRealizarInscricaoNaViagem = ({
    axiosConfig,
    ...props
}: BasicMutationAxiosType<
    { codigoDoAluno: number; codigoDaViagem: number },
    { axiosConfig?: CustomAxiosRequestConfig },
    AxiosResponse<TypeInscricaoDoAlunoNaViagem | undefined>
>) =>
    useMutation({
        ...props,
        mutationKey: ["Domain.Viagem.ObterTokenDePagamento"],
        mutationFn: (params) => {
            return Axios.post(
                `quiron/viagem/area-responsavel/inscricao`,
                { codigoDaViagem: params.codigoDaViagem, codigoDoAluno: params.codigoDoAluno },
                { ...axiosConfig }
            );
        },
    });

/**
 */
export const useObterTokenDePagamento = ({
    axiosConfig,
    ...props
}: BasicMutationAxiosType<
    { codigoDaInscricao: number; codigoDaFormaDePagamento: number },
    { axiosConfig?: CustomAxiosRequestConfig },
    AxiosResponse<{ titulo: Titulo; token: string }>
>) =>
    useMutation({
        ...props,
        mutationKey: ["Domain.Viagem.useObterTokenDePagamento"],
        mutationFn: (params) => {
            return Axios.post(
                `quiron/viagem/area-responsavel/inscricao/${params.codigoDaInscricao}/pagar`,
                { codigoDaFormaDePagamento: params.codigoDaFormaDePagamento },
                { ...axiosConfig }
            );
        },
    });

export function useListarTodasInscricoes(
    config: BasicQueryType<AxiosResponse<IPagina<TypeInscricaoDoAlunoNaViagem>>>,
    filtro: {
        pagina?: IPaginaInfo,
        somenteViagens?: number[],
        intervalo?:[Dayjs,Dayjs] 
    }
){
    const params = {
        ...filtro.pagina,
        somenteViagens: filtro.somenteViagens,
        dataInicial : filtro?.intervalo?.[0]?.toDate?.(),
        dataFinal : filtro.intervalo?.[1]?.toDate?.()
    };

    const query = useQuery({
        queryKey: ["Domain.Viagem", "Domain.Viagem.ListarTodasViagens", params],
        queryFn: async () => {
            return Axios.get<IPagina<TypeInscricaoDoAlunoNaViagem>>(`quiron/viagem/inscricoes`, {
                params,
                paramsSerializer: {
                    indexes: null,
                    dots: true,
                },
            });
        },
        staleTime: 1000 * 60,
        enabled: false,
        ...config,
    });

    return query;
}

export function listarTodasInscricoes({ filtros }:{
    filtros : {
        somenteViagens?: number[],
        intervalo?:[Dayjs,Dayjs]
    }
}) {
    const queryString = RequestUtilitario.objectToQueryString({
        somenteViagens : filtros?.somenteViagens,
        dataInicial : filtros?.intervalo?.[0]?.toDate?.(),
        dataFinal : filtros.intervalo?.[1]?.toDate?.()
    });
    return Axios.get<TypeInscricaoDoAlunoNaViagem[]>(`quiron/viagem/inscricoes/todas?${queryString}`);
}

export default ViagemService;